import React, { Suspense } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";

import PrivateRoutes from "./PrivateRoute";

import ROUTES from "../constants/routes";
import Login from "../components/views/userAuth/Login";
import Signup from "../components/views/userAuth/signup/Signup";
import Sidebar from "../components/sidebar/Sidebar";
import Header from "../components/header/Header";
import AccountIssueBanner from "../components/header/AccountIssueBanner";
import OverageBanner from "../components/header/OverageBanner";
import Footer from "../components/footer/Footer";
import ResetPassword from "../components/views/userAuth/ResetPassword";
import ConfirmEmailChange from "../components/views/userAuth/ConfirmEmailChange";
import ConversionSetup from "../components/views/conversionSetup/ConversionSetup";
import AdminLogin from "../components/adminPanel/adminAuth/AdminLogin";
import AdminTwoFactor from "../components/adminPanel/adminAuth/AdminTwoFactor";
import AdminHeader from "../components/adminPanel/adminHeader/AdminHeader";
import AdminSidebar from "../components/adminPanel/adminSidebar/Sidebar";
import Settings from "../components/views/settings/Settings";
import InstallingSkydive from "../components/views/help/InstallingSkydive";

import { RootState } from "../state_management/reducers";
import { SHARING } from "../share/shareView";
import Loader from "../loader/Loader";

const AnalyticsView = React.lazy(
  () => import("../components/views/analyticsView/AnalyticsView")
);

const ShareViewerPage = React.lazy(() => import("../share/ShareViewerPage"));

const Users = React.lazy(
  () => import("../components/adminPanel/users/UsersList")
);
const Admin = React.lazy(
  () => import("../components/adminPanel/admin/AdminList")
);
const DeletedUsers = React.lazy(
  () => import("../components/adminPanel/deletedUsers/DeletedUsersList")
);
const AuditLog = React.lazy(
  () => import("../components/adminPanel/auditLog/AuditLog")
);
const CancellationFeedback = React.lazy(
  () =>
    import(
      "../components/adminPanel/cancellationFeedback/CancellationFeedback"
    )
);
const AdminDashboard = React.lazy(
  () => import("../components/adminPanel/adminDashboard/AdminDashboard")
);
const OnboardingInsights = React.lazy(
  () => import("../components/adminPanel/onboardingInsights/OnboardingInsights")
);
const SignupJourney = React.lazy(
  () => import("../components/adminPanel/adminDashboard/SignupJourney")
);
const Members = React.lazy(
  () => import("../components/adminPanel/adminDashboard/Members")
);

import "../../src/assets/theme/Styles.scss";
import { useTranslation } from "react-i18next";

interface Props {
  component: React.ComponentType | any;
}


export const WithHeader: React.FC<Props> = (props) => {
  return (
    <>
      <div className="main-container">
        <div className="main-box">
          <Header />
          <div className="common-box">
            <PrivateRoutes {...props} />
          </div>
        </div>
      </div>
    </>
  );
};

export const WithoutHeaderAndSideBar: React.FC<Props> = (props) => {
  return (
    <>
      <div className="main-container">
        {/* 🔴 NOT capped. .main-box caps the shell at --skydive-shell-max and
            centres it, which is right for the dashboard: past that width the
            page-slot grid stretches into dead space and the bubble field
            spreads too wide to read at a glance.

            The two pages using THIS wrapper - Settings and ConversionSetup -
            have no grid and no bubbles. They are forms and tables, and the
            cap only gave them symmetric dead margins on a wide screen. */}
          <div className="main-box main-box--uncapped">
          <div className="common-box w-100">
            <PrivateRoutes {...props} />
          </div>
        </div>
      </div>
    </>
  );
};
export const WithSideBarAndHeader: React.FC<Props> = (props) => {
  return (
    <>
      <Header />
      <div className="main-box">
        <Sidebar />
        <div className="dashboard-container">
          <div className="common-box">
            <PrivateRoutes {...props} />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
export const WithSideBarAndHeaderForAdmin: React.FC<Props> = (props) => {
  return (
    <>
      <div className="admin-main">
        <AdminSidebar />
        <div className="main-wrapper">
          <AdminHeader />
          <div className="main-wrapper-inner">
            <div className="wrapper-pages">
              <PrivateRoutes {...props} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PublicRoutes: React.FC = () => {
  const authReducer: any = useSelector((state: RootState) => state.AuthReducer);
  const adminauthReducer: any = useSelector(
    (state: RootState) => state?.AdminAuthReducer
  );
  const { t: translation } = useTranslation();
  return (
    <>
      <Suspense fallback={<p>{translation("loading")}</p>}>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.SIGNUP} element={<Signup />} />
          {SHARING && <Route path="/s/:token" element={<Suspense fallback={<Loader />}><ShareViewerPage /></Suspense>} />}
          <Route
            path={ROUTES.HELP_INSTALLING_SKYDIVE}
            element={<InstallingSkydive />}
          />
          <Route path="/" element={<Navigate to={process.env.REACT_APP_DEMO === "1" ? ROUTES.ANALYTICS_VIEW.replace("/:id", "") : ROUTES.SIGNUP} />} />

          <Route
            path={
              adminauthReducer && adminauthReducer.isLoggedIn
                ? ROUTES.ANALYTICS_VIEW
                : ROUTES.ANALYTICS_VIEW.replace("/:id", "")
            }
            element={
              (authReducer && authReducer.isLoggedIn) ||
              (adminauthReducer && adminauthReducer.isLoggedIn) ? (
                <WithSideBarAndHeader component={AnalyticsView} />
              ) : (
                <Navigate to={ROUTES.LOGIN} />
              )
            }
          />
          <Route path={ROUTES.EMAIL_VALIDATION} element={<Signup />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
          <Route
            path={ROUTES.CONFIRM_EMAIL_CHANGE}
            element={<ConfirmEmailChange />}
          />
          <Route
            path={
              adminauthReducer && adminauthReducer.isLoggedIn
                ? ROUTES.CONVERSION_SETUP
                : ROUTES.CONVERSION_SETUP.replace("/:id", "")
            }
            element={
              (authReducer && authReducer.isLoggedIn) ||
              (adminauthReducer && adminauthReducer.isLoggedIn) ? (
                <WithoutHeaderAndSideBar component={ConversionSetup} />
              ) : (
                <Navigate to={ROUTES.LOGIN} />
              )
            }
          />

          <Route
            path={
              adminauthReducer && adminauthReducer.isLoggedIn
                ? ROUTES.SETTINGS
                : ROUTES.SETTINGS.replace("/:id", "")
            }
            element={
              authReducer && authReducer.isLoggedIn ? (
                <WithoutHeaderAndSideBar component={Settings} />
              ) : (
                <Navigate to={ROUTES.LOGIN} />
              )
            }
          />
          <Route path={ROUTES.ADMINLOGIN} element={<AdminLogin />} />
          <Route path={ROUTES.ADMIN_TWO_FACTOR} element={<AdminTwoFactor />} />

          <Route
            path={ROUTES.USERS}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={Users} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />

          <Route
            path={ROUTES.ADMIN}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={Admin} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.DELETEDUSERS}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={DeletedUsers} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.AUDITLOG}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={AuditLog} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.CANCELLATIONFEEDBACK}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={CancellationFeedback} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.ADMINDASHBOARD}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={AdminDashboard} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.ONBOARDING_INSIGHTS}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={OnboardingInsights} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.SIGNUP_JOURNEY}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={SignupJourney} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.MEMBERS}
            element={
              adminauthReducer && adminauthReducer.isLoggedIn ? (
                <WithSideBarAndHeaderForAdmin component={Members} />
              ) : (
                <Navigate to={ROUTES.ADMINLOGIN} />
              )
            }
          />
          <Route
            path={ROUTES.RESET_ADMIN_PASSWORD}
            element={<ResetPassword />}
          />
           <Route path="*" element={<p>{translation("no_route_match")}</p>} />
        </Routes>
      </Suspense>
    </>
  );
};

export default PublicRoutes;

